/**
 * MissedStatusDialog Component
 * Modal dialog showing device status at the time of a missed capture
 */

import { Modal, Descriptions, Tag, Space, Typography } from 'antd';
import { MaterialIcon } from '@/components/MaterialIcon';
import type { MissedCapture, StatusSnapshot } from '@/types';

const { Text } = Typography;

interface MissedStatusDialogProps {
  missedCapture: MissedCapture | null;
  open: boolean;
  onClose: () => void;
  timezone?: string; // Device timezone (IANA format, e.g. "Europe/Rome")
}

/**
 * Infer the reason for a missed capture from status snapshot
 */
function inferMissedReason(missed: MissedCapture): string {
  const status = missed.nearestStatusSnapshot;

  if (!status) {
    return 'No status data available';
  }

  // Check capture status
  if (status.capturing === false) {
    return 'Capture was paused';
  }

  // Check battery level
  if (status.battery !== undefined && status.battery < 15) {
    return `Battery critical (${status.battery}%)`;
  }

  // Check network
  if (status.networkType === 'offline') {
    return 'Device was offline';
  }

  // Check app state
  if (status.appState === 'doze') {
    return 'Device was in doze mode';
  }

  return 'Unknown reason';
}

/**
 * Format timestamp for display using device timezone
 */
function formatTime(date: Date, timezone?: string): string {
  return date.toLocaleString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: timezone,
  });
}

/**
 * Format status snapshot timestamp using device timezone
 */
function formatSnapshotTime(snapshot: StatusSnapshot | undefined, timezone?: string): string {
  if (!snapshot?.timestamp) return '—';
  try {
    const date = snapshot.timestamp.toDate
      ? snapshot.timestamp.toDate()
      : new Date(snapshot.timestamp as any);
    return date.toLocaleString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: timezone,
    });
  } catch {
    return '—';
  }
}

/**
 * Get battery color based on level
 */
function getBatteryColor(level: number | undefined): string {
  if (level === undefined) return '';
  if (level < 15) return '#ff4d4f';
  if (level < 30) return '#faad14';
  return '#52c41a';
}

/**
 * Get network type display
 */
function formatNetworkType(type: string | undefined): string {
  if (!type) return '—';
  switch (type) {
    case 'wifi':
      return 'WiFi';
    case 'cellular':
      return 'Cellular';
    case 'offline':
      return 'Offline';
    default:
      return type;
  }
}

/**
 * Get app state display
 */
function formatAppState(state: string | null | undefined): string {
  if (!state) return '—';
  switch (state) {
    case 'foreground':
      return 'Foreground';
    case 'background':
      return 'Background';
    case 'locked':
      return 'Locked';
    case 'doze':
      return 'Doze Mode';
    default:
      return state;
  }
}

export function MissedStatusDialog({
  missedCapture,
  open,
  onClose,
  timezone,
}: MissedStatusDialogProps) {
  if (!missedCapture) return null;

  const status = missedCapture.nearestStatusSnapshot;
  const reason = inferMissedReason(missedCapture);

  return (
    <Modal
      title={
        <Space>
          <MaterialIcon name="error" size={20} style={{ color: '#ff4d4f' }} />
          Missed Capture Details
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
    >
      <Descriptions column={1} bordered size="small" style={{ marginTop: 16 }}>
        {/* Expected time */}
        <Descriptions.Item label="Expected Time">
          <Text strong>{formatTime(missedCapture.expectedAt, timezone)}</Text>
        </Descriptions.Item>

        {/* Status */}
        <Descriptions.Item label="Status">
          <Tag color={missedCapture.status === 'missed' ? 'error' : 'warning'}>
            {missedCapture.status === 'missed' ? 'Missed' : 'Pending'}
          </Tag>
        </Descriptions.Item>

        {/* Inferred reason */}
        <Descriptions.Item label="Likely Reason">
          <Text type={reason === 'Unknown reason' ? 'secondary' : undefined}>
            {reason}
          </Text>
        </Descriptions.Item>
      </Descriptions>

      {status && (
        <>
          <Text
            type="secondary"
            style={{ display: 'block', margin: '16px 0 8px', fontSize: 12 }}
          >
            Device status at {formatSnapshotTime(status, timezone)}
          </Text>

          <Descriptions column={2} bordered size="small">
            {/* Battery */}
            <Descriptions.Item label="Battery">
              <Space>
                <MaterialIcon
                  name={status.batteryCharging ? 'battery_charging_full' : 'battery_std'}
                  size={16}
                />
                <Text style={{ color: getBatteryColor(status.battery) }}>
                  {status.battery !== undefined ? `${status.battery}%` : '—'}
                </Text>
                {status.batteryCharging && (
                  <Tag color="green" style={{ marginLeft: 4 }}>
                    Charging
                  </Tag>
                )}
              </Space>
            </Descriptions.Item>

            {/* Capturing */}
            <Descriptions.Item label="Capturing">
              <Tag color={status.capturing ? 'success' : 'error'}>
                {status.capturing ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>

            {/* Network */}
            <Descriptions.Item label="Network">
              <Space>
                <MaterialIcon
                  name={
                    status.networkType === 'wifi'
                      ? 'wifi'
                      : status.networkType === 'cellular'
                      ? 'signal_cellular_alt'
                      : 'signal_disconnected'
                  }
                  size={16}
                />
                <Text
                  type={status.networkType === 'offline' ? 'danger' : undefined}
                >
                  {formatNetworkType(status.networkType)}
                </Text>
              </Space>
            </Descriptions.Item>

            {/* App state */}
            <Descriptions.Item label="App State">
              <Text>{formatAppState(status.appState)}</Text>
            </Descriptions.Item>
          </Descriptions>
        </>
      )}

      {!status && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Text type="secondary">
            No device status data available for this time period.
          </Text>
        </div>
      )}
    </Modal>
  );
}
